import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { juntarClasses } from './classes';
import { alvoDoPortal } from './portal';
import { IconeCheck, IconeFechar } from './icones';

/**
 * Seleção múltipla com chips — o "Autocomplete multiple" do Deca 1.
 *
 * ## Por que não caixas de seleção
 *
 * O vínculo trabalhista era um grupo de caixas. A operação pediu o modelo do
 * Deca 1 (lista de 15/09, item 13): um campo que se parece com os outros do
 * formulário, com o que foi escolhido em chips e a lista abrindo ao clicar.
 * Cinco caixas soltas quebravam o ritmo do formulário; um campo, não.
 *
 * ## Teclado e leitor de tela
 *
 * O botão abre um `listbox` com `aria-multiselectable`; setas percorrem, Enter
 * ou espaço alterna, Escape fecha. Cada chip tem o seu "remover" com rótulo.
 *
 * ## A lista vai para o `body`
 *
 * O campo vive dentro de modais que rolam (`overflow-y-auto`); uma lista
 * absoluta ali seria cortada na borda. Como o menu "⋯", ela é ancorada pela
 * posição do campo na janela e fecha ao rolar.
 */

export interface PropsCampoDeSelecaoMultipla {
  readonly rotulo: string;
  readonly opcoes: readonly string[];
  readonly escolhidos: readonly string[];
  readonly aoMudar: (v: string[]) => void;
  readonly ajuda?: string;
  readonly placeholder?: string;
  readonly className?: string;
}

export function CampoDeSelecaoMultipla(props: PropsCampoDeSelecaoMultipla): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const [foco, setFoco] = useState(0);
  const raiz = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number; width: number } | null>(null);
  const id = useId();
  const idLista = `${id}-lista`;
  const idAjuda = `${id}-ajuda`;

  const alternar = (opcao: string): void => {
    const sem = props.escolhidos.filter((v) => v !== opcao);
    // Mantém a ordem da lista, como a API grava.
    props.aoMudar(
      props.escolhidos.includes(opcao)
        ? sem
        : props.opcoes.filter((o) => o === opcao || sem.includes(o)),
    );
  };

  useLayoutEffect(() => {
    if (!aberto || campo.current === null) return;
    const r = campo.current.getBoundingClientRect();
    setPosicao({ top: r.bottom + 4, left: r.left, width: r.width });
  }, [aberto, props.escolhidos.length]);

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (ev: MouseEvent): void => {
      const alvo = ev.target as Node;
      if (raiz.current?.contains(alvo) || lista.current?.contains(alvo)) return;
      setAberto(false);
    };
    const aoRolar = (ev: Event): void => {
      // Rolar a própria lista não fecha; rolar a página ou o modal, sim.
      if (lista.current?.contains(ev.target as Node)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    window.addEventListener('scroll', aoRolar, true);
    window.addEventListener('resize', aoRolar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      window.removeEventListener('scroll', aoRolar, true);
      window.removeEventListener('resize', aoRolar);
    };
  }, [aberto]);

  return (
    <div ref={raiz} className={juntarClasses('relative flex flex-col gap-1.5', props.className)}>
      <span id={id} className="text-sm font-medium text-conteudo-secundario">
        {props.rotulo}
      </span>

      <div
        ref={campo}
        role="combobox"
        tabIndex={0}
        aria-labelledby={id}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-controls={idLista}
        aria-describedby={props.ajuda === undefined ? undefined : idAjuda}
        className="flex min-h-11 w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-card border border-borda bg-superficie-elevada px-3 py-1.5 transition-colors hover:border-acento-ciano/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano focus-visible:ring-offset-2 focus-visible:ring-offset-superficie-base"
        onClick={() => setAberto((a) => !a)}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') {
            setAberto(false);
            return;
          }
          if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            ev.preventDefault();
            if (!aberto) {
              setAberto(true);
              return;
            }
            const n = props.opcoes.length;
            setFoco((f) => (ev.key === 'ArrowDown' ? (f + 1) % n : (f - 1 + n) % n));
            return;
          }
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            if (!aberto) setAberto(true);
            else {
              const opcao = props.opcoes[foco];
              if (opcao !== undefined) alternar(opcao);
            }
            return;
          }
          if (ev.key === 'Backspace' && props.escolhidos.length > 0) {
            const ultimo = props.escolhidos[props.escolhidos.length - 1];
            if (ultimo !== undefined) alternar(ultimo);
          }
        }}
      >
        {props.escolhidos.length === 0 ? (
          <span className="text-sm text-conteudo-sutil">{props.placeholder ?? 'Selecione…'}</span>
        ) : (
          props.escolhidos.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full border border-borda bg-superficie-base px-2.5 py-0.5 text-xs text-conteudo-primario"
            >
              {v}
              <button
                type="button"
                aria-label={`Remover ${v}`}
                className="rounded-full text-conteudo-secundario hover:text-conteudo-primario focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano"
                onClick={(ev) => {
                  ev.stopPropagation();
                  alternar(v);
                }}
              >
                <IconeFechar className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
        <span aria-hidden="true" className="ml-auto text-xs text-conteudo-sutil">
          {aberto ? '▲' : '▼'}
        </span>
      </div>

      {aberto &&
        createPortal(
          <ul
            ref={lista}
            id={idLista}
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={id}
            style={{
              position: 'fixed',
              top: posicao?.top ?? -9999,
              left: posicao?.left ?? -9999,
              width: posicao?.width ?? 240,
              zIndex: 60,
            }}
            className="flex max-h-64 flex-col overflow-y-auto rounded-card border border-borda bg-superficie-elevada p-1 shadow-cartao"
          >
            {props.opcoes.map((opcao, i) => {
              const marcado = props.escolhidos.includes(opcao);
              return (
                <li
                  key={opcao}
                  role="option"
                  aria-selected={marcado}
                  className={juntarClasses(
                    'flex cursor-pointer items-center justify-between rounded-card px-3 py-2 text-sm text-conteudo-primario hover:bg-superficie-vidro',
                    i === foco && 'bg-superficie-vidro',
                  )}
                  onMouseEnter={() => setFoco(i)}
                  onClick={() => alternar(opcao)}
                >
                  <span>{opcao}</span>
                  {marcado && <IconeCheck className="h-4 w-4 text-acento-ciano" />}
                </li>
              );
            })}
          </ul>,
          alvoDoPortal(raiz.current),
        )}

      {props.ajuda !== undefined && (
        <p id={idAjuda} className="text-xs text-conteudo-secundario">
          {props.ajuda}
        </p>
      )}
    </div>
  );
}
